import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, LoaderCircle, Share, SquarePlus, EllipsisVertical, X } from 'lucide-react';
import { useInstallState, promptInstall, waitForPrompt, isIOS } from '../lib/install';

// "Install App" in the footer, on phones only (md:hidden), and hidden once Zuzu
// is installed. Android opens the browser's install dialog; iPhone shows how to
// add Zuzu to the home screen, since Apple allows no install button.
export default function InstallApp({ isNight }: { isNight: boolean }) {
  const state = useInstallState();
  const [guide, setGuide] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const close = () => { setGuide(false); buttonRef.current?.focus(); };

  useEffect(() => {
    if (!guide) return;
    sheetRef.current?.querySelector<HTMLElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [guide]);

  if (state === 'installed') return null;

  const onClick = async () => {
    if (state === 'prompt') { await promptInstall(); return; }
    // Android: install directly as soon as the browser is ready. The steps are
    // only for browsers that never offer it (iPhone, Firefox, a private tab).
    if (!isIOS()) {
      setWaiting(true);
      const ready = await waitForPrompt();
      setWaiting(false);
      if (ready) { await promptInstall(); return; }
    }
    setGuide(true);
  };

  const ios = isIOS();
  const step = 'flex items-start gap-3 text-sm text-left';
  const num = `shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${isNight ? 'bg-white/10' : 'bg-black/5'}`;
  const chip = `inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md mx-0.5 align-middle ${isNight ? 'bg-white/10' : 'bg-black/5'}`;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={onClick}
        disabled={waiting}
        aria-busy={waiting}
        className={`md:hidden inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all active:scale-95 ${
          isNight ? 'border-white/15 bg-white/5 text-white/80 hover:text-white' : 'border-black/10 bg-white/50 text-black/70 hover:text-black'
        }`}
      >
        {waiting
          ? <><LoaderCircle size={15} className="animate-spin" aria-hidden="true" /> Getting ready…</>
          : <><Download size={15} aria-hidden="true" /> Install App</>}
      </button>

      {/* On <body>: the footer's blur would trap a fixed sheet inside the footer. */}
      {guide && createPortal(
        <div className="fixed inset-0 z-[95] flex items-end justify-center bg-black/50 p-4" onClick={close}>
          <div
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="install-title"
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-sm rounded-3xl border p-5 pb-6 shadow-2xl ${isNight ? 'bg-[#141427] border-white/10 text-white' : 'bg-white border-black/10 text-black'}`}
          >
            <div className="flex items-center justify-between mb-1">
              <h2 id="install-title" className="text-base font-semibold" style={{ fontFamily: "'Inter', sans-serif" }}>Install Zuzu</h2>
              <button type="button" onClick={close} aria-label="Close" className={`p-1.5 rounded-full ${isNight ? 'hover:bg-white/10' : 'hover:bg-black/5'}`}>
                <X size={16} />
              </button>
            </div>
            <p className={`text-xs mb-4 ${isNight ? 'text-white/60' : 'text-black/55'}`}>
              Zuzu opens from your home screen like an app, and always has the latest updates.
            </p>
            {ios ? (
              <ol className="space-y-3">
                <li className={step}><span className={num}>1</span><span>Tap <span className={chip}><Share size={14} aria-hidden="true" /> Share</span> in the browser bar.</span></li>
                <li className={step}><span className={num}>2</span><span>Scroll down and tap <span className={chip}><SquarePlus size={14} aria-hidden="true" /> Add to Home Screen</span>.</span></li>
                <li className={step}><span className={num}>3</span><span>Tap <b>Add</b>. Zuzu is now on your home screen.</span></li>
              </ol>
            ) : (
              <ol className="space-y-3">
                <li className={step}><span className={num}>1</span><span>Open the browser menu <span className={chip}><EllipsisVertical size={14} aria-hidden="true" /> Menu</span>.</span></li>
                <li className={step}><span className={num}>2</span><span>Tap <b>Install app</b> or <b>Add to Home screen</b>.</span></li>
                <li className={step}><span className={num}>3</span><span>Confirm. Zuzu is now on your home screen.</span></li>
              </ol>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
